import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import Humanize from 'humanize-plus';
import { Breadcrumbs, Flex, FlexColumn } from '../../../components';
import Table, {
  BaseTableProps,
  DisplayPanel,
  GroupedTable,
  QuickFilter,
  TableEmptyState,
  TableFilters,
  ToggleColumnsMenu,
} from '../../../components/Table';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import useNavigateWithEnvParam from '../../../hooks/useNavigateWithEnvParam';
import useSearchParams from '../../../hooks/useSearchParams';
import useSelectedTreeItem from '../../../hooks/useSelectedTreeItem';
import useViewLoadTracking from '../../../hooks/useViewLoadTracking';
import { TEST_RUNS_TABLE_FILTERS } from '../../../constants/urlParams';
import { getValue, isInSaaSMode } from '../../../utils/common';
import { TestRun } from '../../../types';
import { TestRunsProps } from '../types';
import { fontSize, radius, space } from '../../../style/utils';
import { getTestRunsTableColumns } from './columns';

const STATUS_FILTER_COLUMN_ID = 'metadata.latest_run_status';

const DaysBack = styled(Typography)`
  font-size: ${fontSize('sm')};
  color: ${({ theme }) => theme.palette.content.secondary};
  background: ${({ theme }) => theme.palette.surface.subtle};
  padding: ${space('sm', 'lg')};
  border-radius: ${radius('sm')};
`;

const TableWrapper = styled(FlexColumn)`
  background-color: ${({ theme }) => theme.palette.surface.base};
  overflow: hidden;
  width: 100%;
  height: 100%;
  gap: ${space('xxl')};
`;

const TableHeader = styled(Flex)`
  gap: ${space('xxl')};
  padding-bottom: ${space('lg')};
  width: 100%;
  justify-content: space-between;
`;

const TestRuns = ({
  tableData,
  filterOptions,
  isFetching,
  isFilterOptionsFetching,
  fetchNextPage,
  hasNextPage,
  daysBack,
  search,
  onSearch,
  expandedRowRenderer,
  defaultSortKey = 'test_runs.fail_rate',
  defaultSortDirection = 'desc',
  groupBy,
  onGroupByChange,
  groupableColumnIds,
  groupedData,
  expandedGroups,
  onGroupToggle,
  onGroupScrollToBottom,
}: TestRunsProps) => {
  const isSaaS = isInSaaSMode();
  const isGrouped = !!groupedData;
  const { selectedTreeNode } = useSelectedTreeItem();

  const breadcrumbs = useBreadcrumbs({
    pageTitle: 'Test Performance',
  });

  const { id: selectedTestId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigateWithEnvParam();

  const columns = useMemo(
    () => getTestRunsTableColumns(tableData, filterOptions),
    [tableData, filterOptions],
  );

  const filteredColumns = useMemo(
    () =>
      selectedTreeNode &&
      !['test', 'folder'].includes(selectedTreeNode?.resourceType || '')
        ? columns.filter((c) => c.id !== 'metadata.table_name')
        : columns,
    [columns, selectedTreeNode],
  );

  const totals = useMemo(() => {
    const totalsObj = {
      errors: 0,
      failures: 0,
      warnings: 0,
      passed: 0,
      all: 0,
    };

    if (filterOptions) {
      const statusFilterOptions = filterOptions[STATUS_FILTER_COLUMN_ID] || [];
      statusFilterOptions.forEach((option) => {
        totalsObj.all += option.count || 0;
        const value = option.value.toLowerCase();
        if (value === 'error') {
          totalsObj.errors += option.count || 0;
        } else if (value === 'fail') {
          totalsObj.failures += option.count || 0;
        } else if (value === 'warn') {
          totalsObj.warnings += option.count || 0;
        } else if (value === 'pass') {
          totalsObj.passed += option.count || 0;
        }
      });

      return totalsObj;
    }

    // Fallback for when status filterOptions aren't available (OSS/report mode,
    // where the data hook returns filterOptions: null). Count one per row by the
    // row's latest run status so the labels match the quick filters, which filter
    // by metadata.latest_run_status. (In SaaS, filterOptions is always provided,
    // so this branch never runs.)
    tableData.forEach((testRun) => {
      const status = testRun.metadata.latest_run_status?.toLowerCase();
      if (status === 'error') {
        totalsObj.errors += 1;
      } else if (status === 'fail') {
        totalsObj.failures += 1;
      } else if (status === 'warn') {
        totalsObj.warnings += 1;
      } else if (status === 'pass') {
        totalsObj.passed += 1;
      }
    });
    totalsObj.all = tableData.length;

    return totalsObj;
  }, [filterOptions, tableData]);

  const quickFilters: QuickFilter[] = useMemo(
    () => [
      {
        name: `Passed (${Humanize.intComma(totals.passed)})`,
        filters: [
          {
            property: STATUS_FILTER_COLUMN_ID,
            operator: 'equals',
            value: ['pass'],
          },
        ],
      },
      {
        name: `Warnings (${Humanize.intComma(totals.warnings)})`,
        filters: [
          {
            property: STATUS_FILTER_COLUMN_ID,
            operator: 'equals',
            value: ['warn'],
          },
        ],
      },
      {
        name: `Errors (${Humanize.intComma(totals.errors + totals.failures)})`,
        filters: [
          {
            property: STATUS_FILTER_COLUMN_ID,
            operator: 'equals',
            value: ['error', 'fail'],
          },
        ],
      },
    ],
    [totals],
  );

  const onScrollToBottom = useCallback(() => {
    if (fetchNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const tableSearchFunction = useCallback((searchVal: string, row: TestRun) => {
    const searchColumns = [
      'metadata.schema_name',
      'metadata.table_name',
      'metadata.test_name',
      'metadata.column_name',
      'metadata.test_sub_type',
    ];
    return searchColumns.some((col) =>
      getValue(row, col)
        ?.toString()
        .toLowerCase()
        .includes(searchVal.toLowerCase()),
    );
  }, []);

  const hasResults = isGrouped
    ? groupedData.some((group) => (group.count ?? group.data.length) > 0)
    : tableData.length > 0;

  useEffect(() => {
    if (selectedTestId && !isFetching && !isGrouped) {
      const isSelectedTestExists = tableData.some(
        (test) => test.metadata.elementary_unique_id === selectedTestId,
      );

      if (!isSelectedTestExists) {
        navigate(`/report/test-runs?${searchParams.toString()}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTestId, tableData, isFetching, isGrouped]);

  useViewLoadTracking({
    name: 'Test Performance',
    loadDeps: [(hasResults && !isFetching) || null],
  });

  const commonTableProps: BaseTableProps<TestRun> = {
    columns: filteredColumns,
    isLoading: isFetching,
    expandedRowRenderer,
    defaultSortKey,
    defaultSortDirection,
    idKey: 'metadata.elementary_unique_id',
    urlParamsPrefix: TEST_RUNS_TABLE_FILTERS,
    localStorageKey: 'test_runs',
    emptyStateRenderer: TableEmptyState,
    search,
    isFilterOptionsLoading: isFilterOptionsFetching,
  };

  return (
    <Stack sx={{ width: '100%', height: '100%', p: 3, position: 'relative' }}>
      <Stack sx={{ mb: 3, gap: 3, width: '100%' }}>
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Breadcrumbs items={breadcrumbs} />
          <DaysBack>Last {daysBack} Days</DaysBack>
        </Stack>
      </Stack>

      <TableWrapper>
        <TableHeader>
          <Flex gap={space('xxl')}>
            <TableFilters
              data={tableData}
              columns={filteredColumns}
              urlParamsPrefix={TEST_RUNS_TABLE_FILTERS}
              search={search}
              onSearch={onSearch}
              quickFilters={quickFilters}
              clearFiltersChipLabel={`All (${Humanize.intComma(totals.all)})`}
              disabled={isFetching}
            />
          </Flex>
          {isSaaS ? (
            <DisplayPanel
              columns={filteredColumns}
              localStorageKey="test_runs"
              groupBy={groupBy}
              onGroupByChange={onGroupByChange}
              groupableColumnIds={groupableColumnIds}
            />
          ) : (
            <ToggleColumnsMenu
              columns={filteredColumns}
              localStorageKey="test_runs"
            />
          )}
        </TableHeader>
        {isGrouped ? (
          <GroupedTable
            {...commonTableProps}
            data={groupedData}
            expandedGroups={expandedGroups}
            onGroupToggle={onGroupToggle}
            onGroupScrollToBottom={onGroupScrollToBottom}
            showEmptyGroups={false}
            isControlled
            bottomScrollPadding={100}
          />
        ) : (
          <Table
            {...commonTableProps}
            data={tableData}
            scrollItemId={selectedTestId}
            searchFunc={fetchNextPage ? undefined : tableSearchFunction}
            onScrollToBottom={fetchNextPage ? onScrollToBottom : undefined}
            isControlled={!!fetchNextPage}
            bottomScrollPadding={fetchNextPage ? 100 : undefined}
          />
        )}
      </TableWrapper>
    </Stack>
  );
};

export default TestRuns;
